/**
 * client/hud/minimap.ts — BO2 minimap math (pure, no DOM): world -> minimap image UV, the
 * rotating player-centred compass, the heading strip and the forceRadar sweep.
 *
 * OWNER: HUD-UI-AUDIO agent.
 *
 * Sources:
 *  - World -> image mapping replicates maps/mp/_compass.gsc setupminimap(): minimap_corner ents
 *    (3272,3576) and (-3904,-3600), no northyaw so north = +X, scr_RequiredMapAspectratio 1
 *    leaves the square unchanged. u = (3576 - y) / 7176, v = (3272 - x) / 7176; image top = +X
 *    (north = the bow), image left = +Y (west) [hud.md "World -> minimap mapping",
 *    ui-manifest.json minimap.mp_hijacked].
 *  - compassMaxRange default 2500 world units (t6mp dvar dump research/data/dvars_t6mp.json;
 *    mp_hijacked.gsc does not override it, unlike Pod/Studio/... which set 2100).
 *    How the range maps onto the 150 px square is an ESTIMATE: range = centre-to-edge distance.
 *  - compassRotation 1 (T5 default): the map rotates so the view direction points up.
 *  - forceRadar 1 gives every player a permanent UAV (_globallogic_player.gsc:114-119); the
 *    UAV radar refreshes every compassRadarUpdateTime = 4.0 s and pings fade over
 *    compassRadarPingFadeTime = 4.0 s (t6mp dvar dump). "Revealed with a delay"
 *    (MPTIP_WAGER_STICKS_SPYPLANE). Sweep-all-at-once is an ESTIMATE.
 */

export interface MinimapDef {
  /** north-west corner (x, y) world. */
  nw: [number, number];
  /** south-east corner (x, y) world. */
  se: [number, number];
  /** Image path relative to assets/ui. */
  image: string;
}

/** [hud.md, ui-manifest.json minimap.mp_hijacked] */
export const MINIMAP_HIJACKED: MinimapDef = {
  nw: [3272, 3576],
  se: [-3904, -3600],
  image: 'compass/compass_map_mp_hijacked.png',
};

/** compassMaxRange (t6mp default; Hijacked does not override). */
export const COMPASS_MAX_RANGE = 2500;
/** compassRadarUpdateTime / compassRadarPingFadeTime, ms (t6mp dvar defaults). */
export const RADAR_UPDATE_MS = 4000;
export const RADAR_PING_FADE_MS = 4000;

/** World (CoD x,y) -> minimap image UV (0..1, u right, v down). northYaw = 0. */
export function worldToMinimapUV(def: MinimapDef, x: number, y: number, out: [number, number] = [0, 0]): [number, number] {
  // u = dot(P - NW, -west) / dot(NW - SE, west), west = (0,1); v = dot(P - NW, -north) / dot(NW - SE, north)
  out[0] = (def.nw[1] - y) / (def.nw[1] - def.se[1]);
  out[1] = (def.nw[0] - x) / (def.nw[0] - def.se[0]);
  return out;
}

/** World units covered by the whole minimap image (square). */
export function minimapWorldSize(def: MinimapDef): number {
  return def.nw[0] - def.se[0];
}

/**
 * Position of a world point on the rotating, player-centred compass, relative to the compass
 * centre, in virtual pixels (x right, y down). The view direction (yaw) points straight up.
 * `pxPerUnit` = (compass half-size) / COMPASS_MAX_RANGE.
 */
export function compassOffset(
  px: number, py: number, playerX: number, playerY: number, yawDeg: number, pxPerUnit: number, out: [number, number] = [0, 0],
): [number, number] {
  // image-space offset (u right = -Y world, v down = -X world)
  const du = -(py - playerY) * pxPerUnit;
  const dv = -(px - playerX) * pxPerUnit;
  // canvas rotation by +yaw (clockwise on screen) brings the view direction to "up"
  const t = (yawDeg * Math.PI) / 180, c = Math.cos(t), s = Math.sin(t);
  out[0] = du * c - dv * s;
  out[1] = du * s + dv * c;
  return out;
}

/** Rotation (radians, canvas clockwise) applied to the map image for a given view yaw. */
export function compassRotation(yawDeg: number): number {
  return (yawDeg * Math.PI) / 180;
}

/** Heading clockwise from north (deg, 0..360) for a CoD yaw (CCW from +X = north). */
export function headingFromYaw(yawDeg: number): number {
  return ((-yawDeg % 360) + 360) % 360;
}

/**
 * Heading strip: hud_horizontal_compass_minimap.png is 512 px = 360 deg with the N tick at
 * x = 9.5 (measured from the art: N 9.5, E 137, S 265, W 393 -> 128 px per 90 deg).
 * shaderVector0 = 0.67 is read as the visible fraction of the strip (ESTIMATE, hud.md).
 */
export const HEADING_STRIP_N_U = 9.5 / 512;
export const HEADING_STRIP_VISIBLE = 0.67;
/** Texture u at the centre of the strip for a view yaw. */
export function headingStripCenterU(yawDeg: number): number {
  return HEADING_STRIP_N_U + headingFromYaw(yawDeg) / 360;
}

/** forceRadar sweep: returns true when a new radar snapshot is due. */
export class RadarSweep {
  private lastSweep = -Infinity;
  /** Call with client time (ms); returns true if positions should be re-sampled now. */
  due(nowMs: number): boolean {
    if (nowMs - this.lastSweep >= RADAR_UPDATE_MS) {
      this.lastSweep = nowMs;
      return true;
    }
    return false;
  }
  /** Ping alpha (1 at the sweep, fading linearly to 0 over RADAR_PING_FADE_MS). */
  pingAlpha(nowMs: number): number {
    const t = (nowMs - this.lastSweep) / RADAR_PING_FADE_MS;
    return t <= 0 ? 1 : t >= 1 ? 0 : 1 - t;
  }
  /**
   * The sweep found nobody (the first snapshots with the other players have not arrived yet, e.g. the
   * HUD shows before them at match start). Until the first sweep that had pings, re-sweep on the next
   * frame instead of waiting a full compassRadarUpdateTime with an empty radar.
   */
  empty(): void { if (!this.primed) this.lastSweep = -Infinity; }
  /** The sweep had pings: from now on the fixed 4 s cadence applies. */
  hadPings(): void { this.primed = true; }
  private primed = false;
  reset(): void { this.lastSweep = -Infinity; this.primed = false; }
}
